import { SUPABASE_URL, SUPABASE_ANON_KEY, supabase } from "./supabase";

/**
 * Resolve the current session's access token. Used to capture the token
 * up-front when scheduling a deferred write, so that on `pagehide` we can
 * fire the flush synchronously without re-querying auth (which is async
 * and unsafe to await once the page is being torn down).
 */
export async function getCurrentAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Fire a PostgREST request with `keepalive: true` so it can outlive the
 * page being unloaded. Intended only for `pagehide` flush paths — normal
 * code should use `supabase.from(...)` which handles auth refresh, error
 * reporting, and types.
 *
 * Keepalive requests have a ~64KB cumulative body cap per page lifetime
 * and silently drop their response — there is no way to surface errors,
 * since the page is gone. Only use for small, idempotent cleanup writes.
 */
export function keepaliveSupabaseRequest(
  path: string,
  init: { method: "DELETE" | "PATCH"; body?: Record<string, unknown> },
  accessToken: string,
): void {
  const url = `${SUPABASE_URL}/rest/v1/${path}`;
  const headers: Record<string, string> = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
    Prefer: "return=minimal",
  };
  const fetchInit: RequestInit = {
    method: init.method,
    keepalive: true,
    headers,
  };
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    fetchInit.body = JSON.stringify(init.body);
  }
  // Fire-and-forget — there's no meaningful place to report errors after
  // pagehide. The browser handles the request in the background.
  void fetch(url, fetchInit);
}
